<template>
  <v-card id="account-setting-card">
    <!-- tabs -->
    <v-tabs
      v-model="tab"
      show-arrows
    >
      <v-tab
        v-for="(tabItem, index) in tabsComputed"
        :key="index"
      >
        <v-icon
          size="20"
          class="me-3"
        >
          {{ tabItem.icon }}
        </v-icon>
        <span>{{ tabItem.title }}</span>
      </v-tab>
    </v-tabs>

    <!-- tabs item -->
    <v-tabs-items v-model="tab">
      <v-tab-item>
        <account-settings-account
          :key="`account-${action}-${accountSettingData.clientId || 'new'}`"
          :account-data="accountSettingData.account"
          :action="action"
          :client-id="accountSettingData.clientId"
          @new-client-done="newClientDone"
        ></account-settings-account>
      </v-tab-item>

      <v-tab-item>
        <account-settings-household
          :key="`household-${action}-${accountSettingData.clientId || 'new'}`"
          :client-id="accountSettingData.clientId"
          :household-data="accountSettingData.household"
        ></account-settings-household>
      </v-tab-item>

      <v-tab-item>
        <account-settings-info
          :key="`info-${action}-${accountSettingData.clientId || 'new'}`"
          :information-data="accountSettingData.information"
          :client-id="accountSettingData.clientId"
        ></account-settings-info>
      </v-tab-item>
      <v-tab-item>
        <barcode-client
          :key="`barcode-${action}-${accountSettingData.clientId || 'new'}`"
          :account-data="accountSettingData.account"
          :client-id="accountSettingData.clientId"
        ></barcode-client>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import {
  mdiAccountOutline, mdiLockOpenOutline, mdiInformationOutline, mdiAccountGroup, mdiAccountPlus, mdiBarcode,
} from '@mdi/js'
import { ref } from '@vue/composition-api'
import Swal from 'sweetalert2'

// demos
import AccountSettingsAccount from '@/components/users/add-client-account.vue'
import AccountSettingsHousehold from '@/components/users/add-client-household.vue'
import AccountSettingsInfo from '@/components/users/add-client-info.vue'
import BarcodeClient from '@/components/users/barcode-client.vue'
import { axiosInst } from '@/utils/axiosIns'

export default {
  components: {
    AccountSettingsAccount,
    AccountSettingsHousehold,
    AccountSettingsInfo,
    BarcodeClient,
  },
  props: {
    action: {
      type: String,
      required: true,
    },
    clientId: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const tab = ref('')

    const createAccountSettingData = (action, clientId = '') => ({
      clientId,
      account: {
        avatarImg: require('@/assets/images/avatars/1.png'),
        gender: '',
        name: '',
        zipCode: '',
        birthday: '',
        ethnicity: '',
        status: action === 'new' ? 'Active' : '',
      },
      information: {
        notes: '',
      },
      household: [],
    })

    // account settings data
    const accountSettingData = ref(createAccountSettingData(props.action, props.clientId))

    return {
      tab,

      // tabs,
      accountSettingData,
      createAccountSettingData,
      icons: {
        mdiAccountOutline,
        mdiLockOpenOutline,
        mdiInformationOutline,
        mdiAccountGroup,
        mdiAccountPlus,
        mdiBarcode,
      },

      // clientId: undefined
      // accountDataLocale: {}
    }
  },

  computed: {
    tabsComputed() {
      if (this.action === 'new') {
        return [
          { title: 'New Account', icon: mdiAccountPlus },
        ]
      }

      if (this.action === 'edit') {
        return [
          { title: 'Account', icon: mdiAccountOutline },
          { title: 'Household', icon: mdiAccountGroup },
          { title: 'Notes', icon: mdiInformationOutline },
          { title: 'Barcode', icon: mdiBarcode },
        ]
      }

      return []
    },
  },

  watch: {
    action: {
      immediate: true,
      handler() {
        this.syncRouteState()
      },
    },

    clientId() {
      this.syncRouteState()
    },

  },

  mounted() {
    if (this.action === 'edit' && !this.clientId) {
      this.$router.push({ name: 'users-all-clients' })
    }

    return 0
  },

  methods: {
    syncRouteState() {
      this.tab = ''
      this.accountSettingData = this.createAccountSettingData(this.action, this.clientId)

      if (this.action === 'edit') this.editHandler()
    },

    async newClientDone({ newClientId, addVisit }) {
      // console.log('newClientId:', newClientId)
      if (!newClientId) return

      if (addVisit) await this.addVisit(newClientId)

      await this.$router.push({
        name: 'users-edit-client',
        params: {
          clientId: newClientId,
        },
      })
    },

    async editHandler() {
      try {
        const rs = await this.getUserData()

        if (!rs || rs.error) throw new Error('Invalid client data received from server')

        this.accountSettingData = {
          ...this.accountSettingData,
          ...rs,
        }
      } catch (err) {
        console.error('[editHandler] Failed to edit client:', {
          clientId: this.accountSettingData?.clientId,
          error: err.message,
          stack: err.stack,
        })
      }
    },

    async getUserData() {
      try {
        const response = await axiosInst.post('get-client', {
          id: this.accountSettingData.clientId,
        })

        const { data } = response

        if (data.error) {
          throw new Error(data.message || 'Failed to fetch client data')
        }

        const user = data.r2

        // Optional: early guard clauses / normalization
        // eslint-disable-next-line no-underscore-dangle
        if (!user?._id) {
          throw new Error('Client data is missing required fields')
        }

        return {
          // eslint-disable-next-line no-underscore-dangle
          clientId: user._id,

          account: {
            // Prefer backend-provided avatar when possible
            avatarImg: user.avatar ?? require('@/assets/images/avatars/1.png'),
            gender: user.gender ?? '',
            name: user.name?.trim() || '',
            zipCode: user.zip ? String(user.zip).trim() : '',
            birthday: user.birthday ? user.birthday.split('T')[0] : null,
            ethnicity: user.ethnicity ?? '',
            status: user.status === 1 ? 'Active' : 'Inactive',
          },

          information: {
            notes: user.notes ?? '',
          },

          household: user.household.length ? user.household.map(u => ({
            // Map each household item to the desired structure
            hash: `${Date.now()}-${Math.floor(Math.random() * 10000)}`, // Unique hash for frontend use
            ...u,
          })) : [],
        }
      } catch (err) {
        // Better logging in production: structured logging, Sentry, etc.
        console.error('[getUserData] Failed to load user:', {
          clientId: this.accountSettingData?.clientId,
          error: err.message,
          stack: err.stack,
        })

        throw new Error('Unable to load client data. Please try again later.')
      }
    },

    async addVisit(clientId) {
      try {
        const rs = await axiosInst.post('client-post', { clientId })

        if (rs.status === 200 && rs.data.error === false) {
          setTimeout(() => {
            Swal.fire({
              position: 'top-end',
              icon: 'success',
              title: 'Visit has been registered.',
              showConfirmButton: false,
              timer: 1500,
            })
          }, 1600)
        }
      } catch (err) {
        const serverError = err.response.data || {}

        // console.error(serverError)

        if (serverError?.error === true) {
          setTimeout(() => {
            Swal.fire({
              icon: 'error',
              title: 'Failed to add visit',
              text: serverError.r || 'An error occurred while adding the visit.',
            })
          }, 1600)
        }
      }
    },
  },
}
</script>
